/**
 * The staged-source diff and duplicate-version check — §2.8 #113, #114.
 *
 * Lifted out of `StagedActionBar`'s two `useMemo`s. Both are pure, both have
 * more interesting cases than a story can show, and both are about ids rather
 * than pixels — the diff chips are three numbers, and every bug here is a
 * number that is right for the wrong reason.
 *
 * ---
 * **Why the filtering is the hard part.** Two classes of id exist in a
 * version's immutable `source_ids` snapshot that the user cannot see:
 *
 * - **Soft-deleted** sources. The user already removed them, and the snapshot
 *   keeps them because versions are immutable. Counting them again as
 *   "N removed" double-reports an action the user has already taken.
 * - **Synthetic** sources, generated by the backend when the user supplies
 *   none. `/lessons/{id}/sources` filters them out (`is_internal`), so they
 *   never reach the UI — but their ids DO appear in `staged_source_ids`, and
 *   would diff as "+N added" against sources the user cannot see or uncheck.
 *
 * Both are handled by intersecting each side with `activeSourceIds`: the set
 * of visible, active, non-failed sources. Failed ones are excluded by the
 * caller, because a source that can never be ingested should not invite the
 * user to build a version around it.
 */

/** One version's summary, as the diff needs it. */
export interface VersionSummary {
  version: string;
  source_ids: string[];
  is_current?: boolean;
  /** Null on legacy snapshots taken before prompts were captured. */
  user_prompt?: string | null;
}

export interface StagedDiffArgs {
  /** `staged_source_ids` from lesson metadata. Null means "user has not edited". */
  stagedSourceIds?: string[] | null;
  /** The current version's snapshot, or undefined while the list is loading. */
  currentVersionSourceIds?: string[];
  /** Truthy when a staged prompt exists. */
  hasStagedPrompt: boolean;
  /** Visible + active + non-failed source ids. Omit to skip filtering. */
  activeSourceIds?: Set<string>;
  /**
   * False while the current version record has not resolved.
   *
   * Lesson metadata and the version list are fetched in parallel, so metadata
   * can arrive first with a populated `staged_source_ids` while the baseline is
   * still unknown — making every staged source look like an addition for a
   * frame or two. That "+N added" flash is what this guard prevents; the
   * prompt half is still reported, because it needs no baseline.
   */
  currentVersionKnown: boolean;
}

export interface StagedDiff {
  addedCount: number;
  removedCount: number;
  promptChanged: boolean;
  hasDiff: boolean;
}

export function computeStagedDiff({
  stagedSourceIds,
  currentVersionSourceIds,
  hasStagedPrompt,
  activeSourceIds,
  currentVersionKnown,
}: StagedDiffArgs): StagedDiff {
  const promptChanged = Boolean(hasStagedPrompt);

  if (!currentVersionKnown) {
    return {
      addedCount: 0,
      removedCount: 0,
      promptChanged,
      hasDiff: promptChanged,
    };
  }

  const visible = (ids: string[]) =>
    activeSourceIds ? ids.filter(id => activeSourceIds.has(id)) : ids;

  const currentIds = visible(currentVersionSourceIds ?? []);
  // `?? currentIds` — not `?? []`. A null staged list means "the user has not
  // edited", so the baseline IS the current version and the diff is empty. An
  // explicit `[]` means "unchecked everything" and must read as N removed.
  const stagedIds = visible(stagedSourceIds ?? currentIds);

  const staged = new Set(stagedIds);
  const current = new Set(currentIds);

  let addedCount = 0;
  for (const id of staged) if (!current.has(id)) addedCount += 1;
  let removedCount = 0;
  for (const id of current) if (!staged.has(id)) removedCount += 1;

  return {
    addedCount,
    removedCount,
    promptChanged,
    hasDiff: addedCount > 0 || removedCount > 0 || promptChanged,
  };
}

export interface DuplicateVersionArgs {
  /** Nothing to compare when there is no diff. */
  hasDiff: boolean;
  versions?: VersionSummary[];
  stagedSourceIds?: string[] | null;
  currentVersionSourceIds?: string[];
  stagedPrompt?: string | null;
  userPrompt?: string | null;
}

/**
 * The prior version these staged changes would recreate, if any — §2.8 #114.
 *
 * Applying would mint a new version identical to one that already exists, so
 * the bar blocks it and names the match instead. The server enforces the same
 * rule; this exists so the user finds out before pressing the button rather
 * than from an error afterwards.
 *
 * Deliberately NOT matched against:
 *
 * - **the current version** — that case is already "no diff", and reporting it
 *   as a duplicate would tell the user their own starting point is a conflict.
 * - **legacy versions with a null `user_prompt`** — the prompt was not captured
 *   for those, so "the sources match" is not enough to conclude the versions
 *   would be identical. Skipping them errs toward letting the user proceed,
 *   which the server will reject if it is genuinely a duplicate.
 */
export function findDuplicateVersion({
  hasDiff,
  versions,
  stagedSourceIds,
  currentVersionSourceIds,
  stagedPrompt,
  userPrompt,
}: DuplicateVersionArgs): VersionSummary | null {
  if (!hasDiff) return null;
  if (!versions) return null;

  const stagedSorted = [
    ...(stagedSourceIds ?? currentVersionSourceIds ?? []),
  ].sort();
  const effectivePrompt = stagedPrompt ?? userPrompt ?? '';

  return (
    versions.find(v => {
      if (v.is_current) return false;
      if (v.user_prompt == null) return false;
      const vSorted = [...v.source_ids].sort();
      if (vSorted.length !== stagedSorted.length) return false;
      if (vSorted.some((id, i) => id !== stagedSorted[i])) return false;
      return v.user_prompt === effectivePrompt;
    }) ?? null
  );
}

/** `1.0` → `2.0`. Falls back to `2.0` on anything unparseable. */
export function bumpMajor(version: string): string {
  const major = parseInt(version.split('.')[0] ?? '1', 10);
  return `${Number.isFinite(major) ? major + 1 : 2}.0`;
}
